import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, Gear, CurrencyDollar } from "@phosphor-icons/react";

export default function Settings() {
  const { profile, refreshProfile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState({
    work_type_preference: "any",
    salary_min: "",
    salary_max: "",
    excluded_keywords: "",
  });

  useEffect(() => {
    if (profile) {
      setPrefs({
        work_type_preference: profile.work_type_preference || "any",
        salary_min: profile.salary_min || "",
        salary_max: profile.salary_max || "",
        excluded_keywords: (profile.excluded_keywords || []).join(", "),
      });
    }
  }, [profile]);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        work_type_preference: prefs.work_type_preference,
        salary_min: prefs.salary_min ? parseInt(prefs.salary_min) : null,
        salary_max: prefs.salary_max ? parseInt(prefs.salary_max) : null,
        excluded_keywords: prefs.excluded_keywords.split(",").map(k => k.trim()).filter(Boolean),
      };
      await api.put("/profile/preferences", payload);
      await refreshProfile();
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="max-w-[800px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="mb-10">
          <span className="tick-label inline-flex items-center gap-1.5"><Gear size={14} weight="bold"/> Settings</span>
          <h1 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tighter">
            Match Preferences.
          </h1>
          <p className="mt-4 text-ink-soft max-w-2xl">
            Tweak your preferences to improve the AI prescoring. Jobs containing excluded keywords will be heavily penalized.
          </p>
        </div>

        <form onSubmit={saveSettings} className="border border-slate-200 p-6 lg:p-8 space-y-6">
          <div>
            <Label htmlFor="work_type" className="tick-label">Work Type Preference</Label>
            <Select 
              value={prefs.work_type_preference || "any"} 
              onValueChange={(val) => setPrefs({ ...prefs, work_type_preference: val })}
            >
              <SelectTrigger className="mt-2 h-11 w-full rounded-xl border border-slate-200/60 bg-white px-3 text-sm focus:outline-none focus:ring-1 focus:ring-brand">
                <SelectValue placeholder="Select work type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any (Remote, Hybrid, Onsite)</SelectItem>
                <SelectItem value="remote">Remote Only</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
                <SelectItem value="onsite">Onsite Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="salary_min" className="tick-label">Minimum Salary (USD)</Label>
              <div className="relative mt-2">
                <CurrencyDollar size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
                <Input id="salary_min" type="number" placeholder="80000" value={prefs.salary_min} onChange={(e) => setPrefs({ ...prefs, salary_min: e.target.value })} className="rounded-xl pl-9 h-11" />
              </div>
            </div>
            <div>
              <Label htmlFor="salary_max" className="tick-label">Maximum Salary (USD)</Label>
              <div className="relative mt-2">
                <CurrencyDollar size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
                <Input id="salary_max" type="number" placeholder="150000" value={prefs.salary_max} onChange={(e) => setPrefs({ ...prefs, salary_max: e.target.value })} className="rounded-xl pl-9 h-11" />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="excluded_keywords" className="tick-label">Excluded Keywords (comma separated)</Label>
            <Textarea 
              id="excluded_keywords" 
              rows={3} 
              placeholder="crypto, web3, blockchain, unpaid" 
              value={prefs.excluded_keywords} 
              onChange={(e) => setPrefs({ ...prefs, excluded_keywords: e.target.value })} 
              className="rounded-xl mt-2" 
            />
            <p className="text-[11px] text-ink-mute mt-2">Jobs containing these keywords will receive a very low prescore.</p>
          </div>

          <div className="pt-4 border-t border-border flex justify-end">
            <Button type="submit" disabled={saving} className="rounded-xl bg-brand hover:bg-brand-hover text-white uppercase tracking-wider text-xs h-11 px-6">
              {saving ? "Saving…" : "Save Preferences"} <ArrowRight size={14} weight="bold" className="ml-2" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
